/**
 * 표 크기 그리드 피커 — 슬래시 "표"/플로팅 메뉴에서 열림. N×M 그리드를 호버해 크기를 고른다.
 *  - 동적 확장: 선택이 마지막 행/열에 닿으면 그리드가 한 칸씩 자람(초기 5×5 → 최대 MAX).
 *  - 열 상한: getMaxCols()(에디터 폭 기반)로 제한 → 폭 초과 표 생성 예방(#4/#7 연계).
 *  - 커밋(클릭/Enter) 시 onPick(rows, cols). 팝업은 document.body 절대배치, open 시 range 저장.
 * 이모지 피커(attachEmojiPicker) 구조·수명주기 패턴을 따른다.
 */

const INITIAL = 5; // 초기(그리고 최소) 표시 그리드
const MAX = 20; // 하드 상한

const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));

export function attachTableSizePicker(editorEl, opts) {
  opts = opts || {};
  const onPick = typeof opts.onPick === "function" ? opts.onPick : () => {};
  const getMaxCols = typeof opts.getMaxCols === "function" ? opts.getMaxCols : () => MAX;
  const L = (opts.locale && opts.locale.tablePicker) || {};
  const T_ROWS = L.rows || "행";
  const T_COLS = L.cols || "열";

  const pop = document.createElement("div");
  pop.className = "lumir-tablepicker";
  pop.style.position = "absolute";
  pop.style.display = "none";
  pop.setAttribute("contenteditable", "false");
  pop.innerHTML = `<div class="tp-grid"></div><div class="tp-label"></div>`;
  document.body.appendChild(pop);
  const gridEl = pop.querySelector(".tp-grid");
  const labelEl = pop.querySelector(".tp-label");

  let openFlag = false;
  let savedRange = null;
  let hovR = 1, hovC = 1;
  let viewR = 0, viewC = 0;
  let maxColsCap = MAX;

  function ensureGrid() {
    const nr = clamp(hovR + 1, INITIAL, MAX);
    // 열 하한(INITIAL)이 상한(maxColsCap)을 넘지 않도록 — 좁은 에디터(maxCols<5)에서 캡 위반 방지.
    const nc = clamp(hovC + 1, Math.min(INITIAL, maxColsCap), maxColsCap);
    if (nr === viewR && nc === viewC) return;
    viewR = nr; viewC = nc;
    gridEl.style.gridTemplateColumns = `repeat(${viewC}, var(--tp-cell, 16px))`;
    let h = "";
    for (let r = 1; r <= viewR; r++) for (let c = 1; c <= viewC; c++) h += `<span class="tp-cell" data-r="${r}" data-c="${c}"></span>`;
    gridEl.innerHTML = h;
  }
  function paint() {
    ensureGrid();
    const cells = gridEl.querySelectorAll(".tp-cell");
    cells.forEach((el) => {
      const r = +el.dataset.r, c = +el.dataset.c;
      el.classList.toggle("on", r <= hovR && c <= hovC);
    });
    labelEl.textContent = `${hovR}${T_ROWS} × ${hovC}${T_COLS}`;
  }
  function setHover(r, c) {
    hovR = clamp(r, 1, MAX);
    hovC = clamp(c, 1, maxColsCap);
    paint();
  }

  function position() {
    let r = null;
    if (savedRange && savedRange.getBoundingClientRect) {
      const rr = savedRange.getBoundingClientRect();
      if (rr && (rr.width || rr.height || rr.left || rr.top)) r = rr;
    }
    // 접힌 캐럿(빈 문단)은 rect가 0 → 첫 블록으로 폴백되면 기존 표 좌상단에 뜬다.
    // 캐럿이 놓인 블록 콘텐츠의 rect를 써서 실제 삽입 위치(그 문단 아래)에 배치한다.
    if (!r && savedRange) {
      let node = savedRange.startContainer;
      if (node && node.nodeType === 3) node = node.parentNode;
      const host = node && node.closest ? node.closest(".content, .block") : null;
      if (host && host.getBoundingClientRect) {
        const hr = host.getBoundingClientRect();
        if (hr && (hr.width || hr.height || hr.left || hr.top)) r = hr;
      }
    }
    if (!r) {
      // 최후 폴백: 현재 포커스된 콘텐츠(없으면 첫 블록)
      const b =
        (document.activeElement && document.activeElement.closest && document.activeElement.closest(".content")) ||
        editorEl.querySelector(".block .content");
      r = b && b.getBoundingClientRect ? b.getBoundingClientRect() : { left: 0, bottom: 0 };
    }
    // 뷰포트 하단/우측 클램프(측정 후)
    let left = window.scrollX + (r.left || 0);
    let top = window.scrollY + (r.bottom || 0) + 4;
    const pw = pop.offsetWidth || 0, ph = pop.offsetHeight || 0;
    const vw = (document.documentElement && document.documentElement.clientWidth) || window.innerWidth || 0;
    const vh = (document.documentElement && document.documentElement.clientHeight) || window.innerHeight || 0;
    if (vw && pw && left - window.scrollX + pw > vw - 8) left = window.scrollX + vw - pw - 8;
    if (left < window.scrollX + 8) left = window.scrollX + 8;
    if (vh && ph && top - window.scrollY + ph > vh - 8) top = window.scrollY + Math.max(8, (r.top || 0) - ph - 4);
    pop.style.left = left + "px";
    pop.style.top = top + "px";
  }

  function open() {
    openFlag = true;
    const s = window.getSelection();
    savedRange = s && s.rangeCount ? s.getRangeAt(0).cloneRange() : null;
    maxColsCap = clamp(Math.round(getMaxCols()) || MAX, 1, MAX);
    hovR = 1; hovC = 1; viewR = 0; viewC = 0;
    pop.style.display = "block";
    paint();
    position();
  }
  function close() {
    openFlag = false;
    pop.style.display = "none";
  }
  function commit() {
    const r = hovR, c = clamp(hovC, 1, maxColsCap);
    // caret 복원 후 삽입(비동기 호버 동안 selection이 유지되지 않았을 수 있음)
    if (savedRange) { const s = window.getSelection(); s.removeAllRanges(); s.addRange(savedRange); }
    close();
    onPick(r, c);
  }

  function onPopMove(e) {
    const cell = e.target.closest && e.target.closest(".tp-cell");
    if (cell) setHover(+cell.dataset.r, +cell.dataset.c);
  }
  function onPopMousedown(e) {
    e.preventDefault(); // 에디터 caret 유지(포커스 이동 방지)
    const cell = e.target.closest && e.target.closest(".tp-cell");
    if (cell) { setHover(+cell.dataset.r, +cell.dataset.c); commit(); }
  }
  function onDocMousedown(e) {
    if (openFlag && !pop.contains(e.target)) close();
  }
  function onKeydown(e) {
    if (!openFlag) return;
    switch (e.key) {
      case "ArrowRight": e.preventDefault(); setHover(hovR, hovC + 1); break;
      case "ArrowLeft": e.preventDefault(); setHover(hovR, hovC - 1); break;
      case "ArrowDown": e.preventDefault(); setHover(hovR + 1, hovC); break;
      case "ArrowUp": e.preventDefault(); setHover(hovR - 1, hovC); break;
      case "Enter": e.preventDefault(); commit(); break;
      case "Escape": e.preventDefault(); close(); break;
    }
  }
  pop.addEventListener("mousemove", onPopMove);
  pop.addEventListener("mousedown", onPopMousedown);
  document.addEventListener("mousedown", onDocMousedown, true);
  document.addEventListener("keydown", onKeydown, true);

  return {
    open,
    close,
    isOpen: () => openFlag,
    element: pop,
    destroy() {
      document.removeEventListener("mousedown", onDocMousedown, true);
      document.removeEventListener("keydown", onKeydown, true);
      if (pop.parentNode) pop.parentNode.removeChild(pop);
    },
  };
}
